import { useMediaQuery } from "react-responsive";
import {
  tokens,
  Typography,
  SvgIcon,
  SvgNames,
} from "@sunrun/experience-ui-components";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import styled from "@emotion/styled/macro";
import {
  AgendaStep,
  SolarStepsContent,
} from "../../../components/molecules/SolarSteps";
import { OPTY_PROGRESS_STEPS } from "../../../checkout/util/constants";
import { useCapContext } from "../../providers/CapContext";
import { CapSectionTitle } from "../molecules/CapStepSection";
import { shouldShowPostCapHomeCheckScheduler } from "../../constants/postCapHomeCheck";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { useFlag } from "hooks/useFlag";
import { FeatureFlags } from "fixtures/features";
import { FormReferral } from "components/organisms/Referral";
import { getPrimaryContact } from "utils/contacts";

const StepIcon = ({ name }: { name: SvgNames }) => {
  return <SvgIcon name={name} color={tokens.WHITE} height={24} width={24} />;
};

const NextSteps = () => {
  const { prospect, sow, contacts, isCustomer, userProfile } = useCapContext();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const cxProName = prospect?.projectManagerName
    ? `, ${prospect?.projectManagerName},`
    : "";
  const isPpcSchedulingInOneEnabled = useFlag(
    FeatureFlags.PPC_SCHEDULING_IN_ONE
  );
  const showReferralForm =
    !isCustomer && Boolean(userProfile && isSunrunManaged(userProfile));

  return (
    <NextStepsView>
      {shouldShowPostCapHomeCheckScheduler(
        sow?.otherCapReqs,
        sow?.postCapHomeCheckNeeded,
        isPpcSchedulingInOneEnabled
      ) ? (
        <>
          <CapSectionTitle>
            Thank you for completing your Design Approval, here are the next
            steps
          </CapSectionTitle>
          <NextStepsInfoContainer>
            <StepInfo>
              <StepInfoIcon>
                <StepIcon name={SvgNames.Call} />
              </StepInfoIcon>
              <StepInfoText>
                <StepHeading>You will be contacted by your CX Pro</StepHeading>
                <StepBody>
                  Your CX Pro{cxProName ? `, ${cxProName},` : ""} will reach out
                  shortly to coordinate next steps.
                </StepBody>
              </StepInfoText>
            </StepInfo>
            <StepInfo>
              <StepInfoIcon>
                <StepIcon name={SvgNames.Home} />
              </StepInfoIcon>
              <StepInfoText>
                <StepHeading>You might get a home visit</StepHeading>
                <StepBody>
                  As we progress final approvals, Sunrun may reach out to do a
                  short home review and capture any final details before your
                  install.
                </StepBody>
              </StepInfoText>
            </StepInfo>
          </NextStepsInfoContainer>
        </>
      ) : (
        <CapSectionTitle>
          Your CX Pro{cxProName} will reach out shortly to coordinate city
          permitting
        </CapSectionTitle>
      )}

      <SolarStepsContent
        showSolarStepsDetails={true}
        showDoubleColumnStyles={isTablet}
        showSingleColumnStyles={isMobile}
      >
        {OPTY_PROGRESS_STEPS.map((solarStep) => {
          return (
            <AgendaStep
              key={solarStep?.stepTitle}
              showSingleColumnStyles={isMobile}
              showDoubleColumnStyles={isTablet}
              img={solarStep?.img}
              stepNumber={solarStep?.stepTitle}
              description={solarStep?.description}
              color={tokens.BRAND_MEDIUM_BLUE}
              completed={
                solarStep.stepTitle === "Step 1" ||
                solarStep.stepTitle === "Step 2"
              }
              current={solarStep.stepTitle === "Step 3"}
            />
          );
        })}
      </SolarStepsContent>
      {showReferralForm && (
        <ReferralFormContainer>
          <FormReferral contactId={getPrimaryContact(contacts)?.contactId} />
        </ReferralFormContainer>
      )}
    </NextStepsView>
  );
};

const NextStepsView = styled.section`
  box-sizing: border-box;
`;

const ReferralFormContainer = styled.div`
  margin-top: 24px;
  width: 100%;
`;

const NextStepsInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin: 24px 0;

  @media (max-width: ${mobileMaxWidth}px) {
    flex-direction: column;
  }
`;

const StepInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  max-width: 410px;
`;

const StepInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepHeading = styled(Typography)`
  color: ${tokens.BRAND_HERO_BLUE};
  font-size: 18px;
  font-weight: bold !important;
  margin: 0;
`;

const StepBody = styled(Typography)`
  color: ${tokens.BRAND_HERO_BLUE};
  font-size: 16px;
  margin: 0;
`;

const StepInfoIcon = styled.div`
  background-color: ${tokens.INFO_70};
  margin-top: 4px;
  width: 41px;
  min-width: 41px;
  height: 41px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export { NextSteps };
